import { useEffect, useRef, useState } from "react";
import { CheckCircle2, RotateCcw, Play } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import GameShell, { shuffle } from "./GameShell";

export default function SightWordsGame() {
    const [words, setWords] = useState([]);
    const [running, setRunning] = useState(false);
    const [time, setTime] = useState(60);
    const [idx, setIdx] = useState(0);
    const [correct, setCorrect] = useState(0);
    const [done, setDone] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        api.get("/games/sight-words").then((r) => setWords(shuffle(r.data)));
    }, []);

    useEffect(() => {
        if (!running) return;
        timerRef.current = setInterval(() => {
            setTime((t) => {
                if (t <= 1) {
                    clearInterval(timerRef.current);
                    finish();
                    return 0;
                }
                return t - 1;
            });
        }, 1000);
        return () => clearInterval(timerRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [running]);

    const start = () => {
        setIdx(0); setCorrect(0); setTime(60); setDone(false); setRunning(true);
    };

    const finish = async () => {
        setRunning(false); setDone(true);
        try {
            await api.post("/progress/game-score", { game: "sight-words", score: correct * 3, correct, total: idx });
            toast.success(`+${correct * 3} XP earned`);
        } catch {}
    };

    const knew = () => { setCorrect((c) => c + 1); next(); };
    const skip = () => { next(); };
    const next = () => {
        if (idx + 1 >= words.length) { finish(); return; }
        setIdx(idx + 1);
    };

    if (!words.length) return <GameShell title="Sight Words Sprint"><p>Loading…</p></GameShell>;

    return (
        <GameShell
            title="Sight Words Sprint"
            intro="60 seconds. Read each word out loud. Tap 'I know it' or 'Skip'."
            testId="sight-words-game"
            instructions="This is the Sight Words Sprint. You have sixty seconds. A common word will appear. Read it out loud, then tap I know it if you got it, or Skip if you did not. How many can you read?"
        >
            <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-[#819888]">Time: <span className="text-[#1C2321] font-semibold" data-testid="sw-timer">{time}s</span></p>
                <p className="text-sm text-[#2D5D4E] font-medium" data-testid="sw-score">Read: {correct}</p>
            </div>

            {!running && !done && (
                <div className="card-rw p-10 text-center">
                    <p className="text-lg text-[#515B57] mb-6">Ready when you are. Tap start.</p>
                    <button onClick={start} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-lg font-medium hover:bg-[#1F4237]" data-testid="sw-start">
                        <Play className="w-5 h-5" /> Start
                    </button>
                </div>
            )}

            {running && (
                <>
                    <div className="card-rw p-14 mb-6 text-center" data-testid="sw-card">
                        <p className="text-6xl sm:text-7xl font-semibold text-[#1C2321]" style={{ fontFamily: "Outfit" }}>
                            {words[idx]}
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <button onClick={skip} className="tile py-5 text-lg" data-testid="sw-skip">Skip</button>
                        <button onClick={knew} className="tile tile-correct py-5 text-lg" data-testid="sw-knew">I know it</button>
                    </div>
                </>
            )}

            {done && (
                <div className="card-rw p-10 text-center">
                    <CheckCircle2 className="w-12 h-12 text-[#3A7D5C] mx-auto mb-4" strokeWidth={1.6} />
                    <h2 className="text-2xl font-medium mb-2" style={{ fontFamily: "Outfit" }}>Time!</h2>
                    <p className="text-lg text-[#515B57] mb-6">You read {correct} sight words.</p>
                    <button onClick={start} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium" data-testid="sw-replay">
                        <RotateCcw className="w-4 h-4" /> Try again
                    </button>
                </div>
            )}
        </GameShell>
    );
}
